import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  coordinatesToDisplayBounds,
  pointToDisplayPosition,
  polygonToDisplayPositions,
  type DisplayBounds,
} from "../../utils/coordinates";
import type { VideoLayout } from "../../utils/videoLayout";
import type { OverlayInteractionEvent, PreparedSpatialSegment } from "./types";
import { useTimelineStore } from "../../store/timeline-context";
import { BoxRenderer } from "./renderers/BoxRenderer";
import { PointRenderer } from "./renderers/PointRenderer";
import { PolygonRenderer } from "./renderers/PolygonRenderer";
import { EMPTY_IDS } from "./styles";
import { FocusMask } from "./FocusMask";
import { focusTargetId, resolveAnnotationFocus, type AnnotationFocusIndex } from "../../utils/focus";
import { toPresentationUs } from "../../utils/time";

/** Matches `animate-fade-in` and the image overlay's dim-mask transition. */
const MASK_FADE_MS = 300;

interface AnnotationLayerProps {
  segments: PreparedSpatialSegment[];
  currentTime?: number;
  videoLayout: VideoLayout;
  onSeek?: (time: number) => void;
  focusIndex?: AnnotationFocusIndex;
}

export const AnnotationLayer: React.FC<AnnotationLayerProps> = ({
  segments,
  currentTime,
  videoLayout,
  onSeek,
  focusIndex,
}) => {
  const setHighlightedSegmentIds = useTimelineStore((s) => s.setHighlightedSegmentIds);
  const toggleSegment = useTimelineStore((s) => s.toggleSegment);
  const selectedId = useTimelineStore((s) => s.selectedSegmentId);
  const highlightedIds = useTimelineStore((s) => s.highlightedSegmentIds);
  const hoveredTimelineSegmentId = useTimelineStore((s) => s.hoveredTimelineSegmentId);
  const hoveredRange = useTimelineStore((s) => s.hoveredTimelineSegmentRange);
  const focus = useMemo(
    () => (focusIndex ? resolveAnnotationFocus(focusIndex, selectedId, highlightedIds) : null),
    [focusIndex, selectedId, highlightedIds],
  );
  // `segments` already contains only this decoded frame's annotations. Both
  // anchors and tracker predictions retain their timeline cluster ID, so a box
  // from another cluster of the same track cannot activate a hovered bar.
  // Match decoded-frame selection's microsecond precision at fragment edges.
  const frameTime = currentTime === undefined ? undefined : toPresentationUs(currentTime);
  const hoverMatchesFrame =
    focus?.mode !== "hover" ||
    hoveredTimelineSegmentId === null ||
    ((!hoveredRange ||
      frameTime === undefined ||
      (frameTime >= toPresentationUs(hoveredRange.start) && frameTime < toPresentationUs(hoveredRange.end))) &&
      segments.some((seg) => (seg.interactionId ?? seg.id) === hoveredTimelineSegmentId));
  const isFocused = (seg: PreparedSpatialSegment) =>
    hoverMatchesFrame && (focus?.ids.has(seg.trackId ?? focusTargetId(seg.id)) ?? false);

  const handleInteraction = useCallback(
    (event: OverlayInteractionEvent) => {
      const canFocus = focusIndex?.has(focusTargetId(event.annotationId)) ?? false;
      if (canFocus && event.type === "mouseenter") {
        setHighlightedSegmentIds([event.annotationId]);
      } else if (canFocus && event.type === "mouseleave") {
        setHighlightedSegmentIds(EMPTY_IDS);
      }

      if (event.type === "click") {
        const seg = segments.find((s) => s.id === event.annotationId);
        if (canFocus && seg?.type === "point_box") {
          toggleSegment(seg.trackId ?? focusTargetId(seg.id));
        } else if (seg) {
          onSeek?.(seg.startTime);
        }
      }
    },
    [setHighlightedSegmentIds, toggleSegment, onSeek, segments, focusIndex],
  );

  // The layout can be unmeasured between sources; everything below still has to
  // run so the hook order never changes, and the guard is applied at the end.
  const { width, height, offsetX, offsetY } = videoLayout;
  const hasLayout = width > 0 && height > 0;
  const focusedBoxes = hasLayout
    ? segments.flatMap((seg) => {
        if (seg.type !== "point_box" || !isFocused(seg)) return [];
        const bounds = coordinatesToDisplayBounds(seg.coordinates, width, height, offsetX, offsetY);
        return bounds.width > 0 && bounds.height > 0 ? [bounds] : [];
      })
    : [];
  const showMask = hasLayout && !!focus?.hasBoxTargets && (focusedBoxes.length > 0 || focus.mode === "pinned");

  // Unmounted only once the fade has run. Dropping it the moment focus goes
  // would rebuild the element on the way out, and a new element starts at its
  // final opacity with nothing to animate from — no fade would be seen.
  const [maskMounted, setMaskMounted] = useState(false);
  const lastMaskBoxes = useRef<DisplayBounds[]>(focusedBoxes);
  if (showMask) {
    lastMaskBoxes.current = focusedBoxes;
    if (!maskMounted) setMaskMounted(true);
  }
  useEffect(() => {
    if (showMask || !maskMounted) return;
    const timer = setTimeout(() => setMaskMounted(false), MASK_FADE_MS);
    return () => clearTimeout(timer);
  }, [showMask, maskMounted]);

  if (!hasLayout) return null;

  return (
    <>
      {maskMounted && (
        <FocusMask
          boxes={showMask ? focusedBoxes : lastMaskBoxes.current}
          exiting={!showMask}
          videoLayout={videoLayout}
        />
      )}
      {segments.map((seg) => {
        if (seg.type === "point_box") {
          const bounds = coordinatesToDisplayBounds(seg.coordinates, width, height, offsetX, offsetY);
          if (bounds.width <= 0 || bounds.height <= 0) return null;
          return (
            <BoxRenderer
              annotation={seg}
              bounds={bounds}
              focused={isFocused(seg)}
              key={seg.trackId ?? seg.id}
              pinned={selectedId === (seg.trackId ?? focusTargetId(seg.id))}
              showLabel={focus?.showLabel ?? false}
              videoLayout={videoLayout}
              onInteraction={handleInteraction}
            />
          );
        }

        if (seg.type === "point") {
          const { x, y } = pointToDisplayPosition(seg.coordinates, width, height, offsetX, offsetY);
          return <PointRenderer annotation={seg} key={seg.id} x={x} y={y} onInteraction={handleInteraction} />;
        }

        return (
          <PolygonRenderer
            annotation={seg}
            containerHeight={height + offsetY * 2}
            containerWidth={width + offsetX * 2}
            key={seg.id}
            points={polygonToDisplayPositions(seg.coordinates, width, height, offsetX, offsetY)}
            onInteraction={handleInteraction}
          />
        );
      })}
    </>
  );
};
